import React, { useState, useEffect } from 'react';
import { projectsAPI, teamsAPI, appAPI } from '../../services/api';

const API_BASE = (process.env.REACT_APP_API_URL || 'https://apimon.mf-league.com/api').replace('/api', '');

const ENDPOINTS = [
  { key: 'table',     label: 'Table',     description: 'League standings table' },
  { key: 'schedule',  label: 'Schedule',  description: 'Game schedule & fixtures' },
  { key: 'roster',    label: 'Roster',    description: 'Team squad (requires teamId)' },
  { key: 'games',     label: 'Games',     description: 'Game results & details' },
  { key: 'standings', label: 'Standings', description: 'Real-time standings alias' },
];

const SLOTS = [1, 2, 3];

const initialConfig = () => {
  const c = {};
  ENDPOINTS.forEach(({ key }) => SLOTS.forEach(s => { c[`${key}${s}`] = ''; }));
  return c;
};

const AppSettings = () => {
  const [projects, setProjects] = useState([]);
  const [teamsByProject, setTeamsByProject] = useState({});
  const [appConfig, setAppConfig] = useState(initialConfig());
  const [selectedTeamForTest, setSelectedTeamForTest] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, configRes] = await Promise.all([
        projectsAPI.getAll(),
        appAPI.getConfig().catch(() => ({ data: {} })),
      ]);

      const projectList = projectsRes.data.projects || projectsRes.data || [];
      setProjects(projectList);

      const savedConfig = configRes.data && Object.keys(configRes.data).length > 0
        ? configRes.data
        : {};
      const merged = { ...initialConfig(), ...savedConfig };
      setAppConfig(merged);

      // F6: load teams for all pre-configured roster slots
      const rosterProjectIds = SLOTS
        .map(s => merged[`roster${s}`])
        .filter(Boolean);
      const uniqueIds = [...new Set(rosterProjectIds)];
      await Promise.all(uniqueIds.map(pid => loadTeamsForProject(pid)));

    } catch (err) {
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamsForProject = async (projectId) => {
    if (!projectId || teamsByProject[projectId]) return;
    try {
      // F1: use teamsAPI.getByProject (goes through axios with JWT)
      const res = await teamsAPI.getByProject(projectId);
      const list = res.data || [];
      setTeamsByProject(prev => ({ ...prev, [projectId]: list }));
    } catch {
      setTeamsByProject(prev => ({ ...prev, [projectId]: [] }));
    }
  };

  const handleConfigChange = (endpointKey, slot, projectId) => {
    const key = `${endpointKey}${slot}`;
    setAppConfig(prev => ({ ...prev, [key]: projectId }));
    if (endpointKey === 'roster' && projectId) {
      loadTeamsForProject(projectId);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // F7: removed debug console.logs
      const filteredConfig = Object.fromEntries(
        Object.entries(appConfig).filter(([, v]) => v && v !== '')
      );
      await appAPI.saveConfig(filteredConfig);
      setSuccess('Configuration saved successfully.');
    } catch (err) {
      setError(err.response?.status === 404
        ? 'App API configuration endpoint not found on server'
        : 'Failed to save configuration'
      );
    } finally {
      setSaving(false);
    }
  };

  const getEndpointUrl = (endpointKey, slot) => {
    const base = `${API_BASE}/appapi/${endpointKey}/${slot}`;
    return endpointKey === 'roster' ? `${base}?teamId=TEAM_ID` : base;
  };

  const handleTestEndpoint = (endpointKey, slot) => {
    let url = `${API_BASE}/appapi/${endpointKey}/${slot}`;
    if (endpointKey === 'roster') {
      const teamId = selectedTeamForTest[`${endpointKey}${slot}`];
      if (!teamId) { alert('Select a team first'); return; }
      url += `?teamId=${teamId}`;
    }
    window.open(url, '_blank');
  };

  const getProjectName = (pid) => projects.find(p => p._id === pid)?.name || '';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <span className="loading loading-spinner loading-md text-blue-500"></span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">App API Configuration</h2>
          <p className="text-sm text-slate-500 mt-1">
            Map each slot to a project. Mobile apps use static endpoints — no app update needed when changing seasons.
          </p>
        </div>
        <button
          className="btn btn-primary btn-sm flex-shrink-0"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <span className="loading loading-spinner loading-xs"></span> : null}
          {saving ? 'Saving…' : 'Save Configuration'}
        </button>
      </div>

      {error && (
        <div className="alert alert-error py-2 text-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success py-2 text-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          {success}
        </div>
      )}

      {/* Endpoint tables */}
      {ENDPOINTS.map((ep) => (
        <div key={ep.key} className="card bg-base-100 border border-slate-200 shadow-sm">
          <div className="card-body p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-slate-800">{ep.label}</h3>
                <p className="text-xs text-slate-500">{ep.description}</p>
              </div>
              <span className="badge badge-ghost text-xs font-mono">/appapi/{ep.key}/:slot</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {SLOTS.map((slot) => {
                const configKey = `${ep.key}${slot}`;
                const selectedPid = appConfig[configKey];
                const isConfigured = !!selectedPid;
                const rosterTeams = ep.key === 'roster' && selectedPid
                  ? (teamsByProject[selectedPid] || [])
                  : [];

                return (
                  <div
                    key={slot}
                    className={`rounded-lg border p-4 space-y-3 ${
                      isConfigured ? 'border-blue-200 bg-blue-50/40' : 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    {/* Slot header */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500">Slot {slot}</span>
                      <span className={`badge badge-sm font-mono ${isConfigured ? 'badge-primary' : 'badge-ghost'}`}>
                        {ep.key}{slot}
                      </span>
                    </div>

                    {/* Project selector */}
                    <div>
                      <label className="label py-0 mb-1">
                        <span className="label-text text-xs text-slate-600">Project</span>
                      </label>
                      <select
                        value={selectedPid || ''}
                        onChange={(e) => handleConfigChange(ep.key, slot, e.target.value)}
                        className="select select-bordered select-xs w-full"
                      >
                        <option value="">— not configured —</option>
                        {projects.map((p) => (
                          <option key={p._id} value={p._id}>{p.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Status */}
                    {isConfigured && (
                      <div className="flex items-center gap-1.5 text-xs text-blue-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        {getProjectName(selectedPid)}
                      </div>
                    )}

                    {/* Roster team selector */}
                    {ep.key === 'roster' && selectedPid && (
                      <div>
                        <label className="label py-0 mb-1">
                          <span className="label-text text-xs text-slate-600">Team for test</span>
                        </label>
                        <select
                          className="select select-bordered select-xs w-full"
                          value={selectedTeamForTest[configKey] || ''}
                          onChange={(e) => setSelectedTeamForTest(prev => ({ ...prev, [configKey]: e.target.value }))}
                        >
                          <option value="">— select team —</option>
                          {rosterTeams.map(t => (
                            <option key={t._id} value={t._id}>{t.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Endpoint URL */}
                    <div className="font-mono text-xs text-slate-500 bg-white border border-slate-200 rounded px-2 py-1.5 break-all leading-relaxed">
                      {getEndpointUrl(ep.key, slot)}
                    </div>

                    {/* Test button */}
                    {isConfigured && (
                      <button
                        className="btn btn-outline btn-xs w-full gap-1"
                        onClick={() => handleTestEndpoint(ep.key, slot)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                        Test in browser
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      {/* Info box */}
      <div className="card bg-blue-50 border border-blue-200">
        <div className="card-body p-5">
          <h4 className="font-semibold text-blue-800 text-sm mb-2">How it works</h4>
          <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
            <li>Mobile apps call static endpoints like <code className="font-mono text-xs">/appapi/table/1</code></li>
            <li>The server returns data from the configured project for that slot</li>
            <li>Change the project mapping here — no app update required</li>
            <li>Roster endpoints require an additional <code className="font-mono text-xs">?teamId=</code> parameter</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AppSettings;
